/**
 * useScanInput — barcode wedge-scanner bursts (design/ux/01_foundations.md
 * §4.8, WP-17).
 *
 * Two uses, one detector (lib/scan-detector.js, thresholds in
 * config/scanner.config.js, per-workstation gap and beep):
 *
 *  · In a scan-aware field — `useScanInput(onScan, { inputRef })`: a burst
 *    typed into that field ends with its terminator swallowed (Entrée never
 *    picks a fuzzy option, Tab never leaves) and `onScan(code)` runs, which
 *    skips the debounce with an exact lookup (Combobox `runExact`, the
 *    caisse search, the product-form barcode field).
 *  · A scan zone — `useScanInput(onScan, { inputRef: zoneRef, zone: true })`
 *    (refonte WP-24, design/ux/02 §1.6 « Routing »): a burst typed into ANY
 *    field of the zone (the S1 entry band, the S2 creation panel) goes to the
 *    handler and is removed from the field it landed in — the field gets back
 *    the value it held before the burst (never a N° lot, a quantity or a
 *    product name made of digits, AUD-BARC-11).
 *  · Focus outside any text field — `useScanInput(onScan)`: the screen's
 *    handler receives the code (the innermost mounted screen wins). A screen
 *    with no handler shows « Code scanné non utilisé sur cet écran ». The
 *    shell installs the single window listener (`installScanListener`).
 *
 * Feedback: the caller flashes / announces its target row; the optional beep
 * (off by default, OP-ACH-25) is played here.
 */
import * as React from "react";
import { createScanDetector, readScannerPrefs } from "@/lib/scan-detector.js";
import { isTextField } from "@/lib/key-combo.js";
import { SCAN_BEEP, SCAN_UNUSED_MESSAGE } from "@/config/scanner.config.js";
import { isModalOverlayOpen, notifyToast, useCommitEffect } from "@/hooks/useShortcuts.js";

let audioCtx = null;
/** Short beep when the workstation asked for it (never by default). */
export function playScanBeep(prefs = readScannerPrefs()) {
  if (!prefs.beep || typeof window === "undefined") return;
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx ?? new Ctx();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.frequency.value = SCAN_BEEP.FREQUENCY_HZ;
    gain.gain.value = SCAN_BEEP.GAIN;
    osc.connect(gain).connect(audioCtx.destination);
    osc.start();
    osc.stop(audioCtx.currentTime + SCAN_BEEP.DURATION_MS / 1000);
  } catch {
    /* no audio device: silent */
  }
}

// ── Window-level scans (focus outside a text field) ─────────────────────────

/** Mounted screen handlers, innermost (last mounted) first to answer. */
const screenHandlers = [];

/**
 * Register a window-level scan handler (what `useScanInput(onScan)` does).
 * @param {{ current: Function | null }} ref  handler holder (read at scan time)
 * @param {boolean} [overlay] the handler belongs to a modal dialog
 * @returns {() => void} unregister
 */
export function registerScanHandler(ref, overlay = false) {
  const entry = { ref, overlay };
  screenHandlers.push(entry);
  return () => {
    const i = screenHandlers.indexOf(entry);
    if (i >= 0) screenHandlers.splice(i, 1);
  };
}

/**
 * Install the single window listener. Idempotent; returns the uninstall.
 * @param {Window} [target]
 */
let windowInstall = null;
export function installScanListener(target = typeof window !== "undefined" ? window : null) {
  if (!target) return () => {};
  if (windowInstall) {
    windowInstall.count += 1;
    return windowInstall.release;
  }
  let detector = createScanDetector({ maxGapMs: readScannerPrefs().maxGapMs });
  const onKeyDown = (e) => {
    if (e.isComposing) return;
    if (isTextField(e.target)) {
      detector.reset();
      return;
    }
    const hit = detector.feed(e);
    if (!hit) return;
    // The terminator must not activate the focused button / row.
    e.preventDefault();
    e.stopPropagation();
    // Under a modal only a handler of that dialog may receive the code.
    const modal = isModalOverlayOpen();
    const handler = [...screenHandlers].reverse().find((h) => !modal || h.overlay);
    if (!handler) {
      notifyToast(SCAN_UNUSED_MESSAGE, "info");
      return;
    }
    playScanBeep();
    handler.ref.current?.(hit.code, { source: "window" });
  };
  // A preference change (display panel) takes effect on the next burst.
  const onPrefs = () => {
    detector = createScanDetector({ maxGapMs: readScannerPrefs().maxGapMs });
  };
  target.addEventListener("keydown", onKeyDown, true);
  target.addEventListener("pharmakon:scanner-prefs", onPrefs);
  windowInstall = {
    count: 1,
    release: () => {
      windowInstall.count -= 1;
      if (windowInstall.count > 0) return;
      target.removeEventListener("keydown", onKeyDown, true);
      target.removeEventListener("pharmakon:scanner-prefs", onPrefs);
      windowInstall = null;
    },
  };
  return windowInstall.release;
}

/**
 * Give a text field back the value it held before a burst (zone mode). The native setter + an
 * `input` event: a React-controlled field sees an ordinary change to that value.
 * @param {HTMLInputElement | HTMLTextAreaElement | null} el
 * @param {string} valeur
 */
export function restaurerValeurChamp(el, valeur) {
  if (!el || !isTextField(el) || String(el.tagName).toUpperCase() === "SELECT" || el.value === valeur) return;
  const Ctor = typeof window !== "undefined" ? (el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement : window.HTMLInputElement) : null;
  const setter = Ctor ? Object.getOwnPropertyDescriptor(Ctor.prototype, "value")?.set : null;
  if (setter && el instanceof Ctor) setter.call(el, valeur);
  else el.value = valeur;
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

/**
 * The keydown listener of a scan-aware field or zone (pure of React; tested with plain events).
 * Zone mode remembers the text field a burst started in and its value then, and gives it back
 * when the burst turns out to be a scan.
 *
 * @param {{ zone?: boolean, maxGapMs: number, onHit: (code: string) => void }} opts
 * @returns {{ onKeyDown: (e: KeyboardEvent) => void, reset: () => void }}
 */
export function creerEcouteurScan({ zone = false, maxGapMs, onHit }) {
  const detector = createScanDetector({ maxGapMs });
  let avant = null;
  let derniere = -Infinity;
  return {
    onKeyDown(e) {
      if (e.isComposing) return;
      if (zone && typeof e.key === "string" && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        const t = Number(e.timeStamp) || 0;
        if (detector.pending() === "" || t - derniere > maxGapMs) {
          avant = isTextField(e.target) ? { el: e.target, valeur: e.target.value ?? "" } : null;
        }
        derniere = t;
      }
      const hit = detector.feed(e);
      if (!hit) return;
      e.preventDefault();
      e.stopPropagation();
      if (zone && avant) restaurerValeurChamp(avant.el, avant.valeur);
      avant = null;
      onHit(hit.code);
    },
    reset() {
      detector.reset();
      avant = null;
    },
  };
}

/**
 * @param {(code: string, meta: { source: "field" | "window" }) => void} onScan
 * @param {{ inputRef?: { current: HTMLElement | null }, enabled?: boolean, overlay?: boolean, zone?: boolean }} [opts]
 *   overlay: the handler belongs to a modal dialog (receives scans while it is open)
 *   zone: `inputRef` is a container; the text field a burst landed in gets its value back
 */
export function useScanInput(onScan, opts = {}) {
  const { inputRef, enabled = true, overlay = false, zone = false } = opts;
  const ref = React.useRef(onScan);
  useCommitEffect(() => {
    ref.current = onScan;
  });

  // Field mode.
  React.useEffect(() => {
    if (!enabled || !inputRef) return undefined;
    const el = inputRef.current;
    if (!el) return undefined;
    const ecouteur = creerEcouteurScan({
      zone,
      maxGapMs: readScannerPrefs().maxGapMs,
      onHit: (code) => {
        playScanBeep();
        ref.current?.(code, { source: "field" });
      },
    });
    const onBlur = (e) => {
      // A zone keeps its burst across its own fields; a field resets on leaving it.
      if (zone && e.relatedTarget instanceof Node && el.contains(e.relatedTarget)) return;
      ecouteur.reset();
    };
    el.addEventListener("keydown", ecouteur.onKeyDown);
    el.addEventListener(zone ? "focusout" : "blur", onBlur);
    return () => {
      el.removeEventListener("keydown", ecouteur.onKeyDown);
      el.removeEventListener(zone ? "focusout" : "blur", onBlur);
    };
  }, [enabled, inputRef, zone]);

  // Window mode (no field).
  React.useEffect(() => {
    if (!enabled || inputRef) return undefined;
    return registerScanHandler(ref, overlay);
  }, [enabled, inputRef, overlay]);
}

export default useScanInput;
